import { usePageTitle } from "../../../../../shared/hooks/usePageTitle";
import { useEffect, useRef, useState } from "react";
import { radioKeys } from "./radioKeys";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../../i18n/useTranslation";
import type { PublicProfessionalProfile } from "../../features/profile/operations";
import type { useFlowState } from "../../features/booking/useFlowState";
import { upcomingDays } from "../../features/booking/days";
import { useAvailableSlots } from "../../features/booking/useAvailableSlots";
import { useHold } from "../../features/booking/useHold";
import { warmGuestIdentity } from "../../lib/firebase";
import { TimeGridSkeleton } from "./StepSkeleton";
import { serviceSummary, StepLeadBar, StepFoot, StepMeta } from "./StepChrome";
import { FlowFrame, requestStepFocus } from "../../components/PageShell";
import { LoadFailed } from "../../components/LoadFailed";

interface Props {
  verification?: boolean;
  /**
   * A guest booking sent from Review may have gone through and is not yet
   * resolved (rc.11 1c, QE P2). The expired-hold notice then says to check the
   * result, never "choose your time again".
   */
  unresolved?: boolean;
  slug: string;
  profile: PublicProfessionalProfile;
  flow: ReturnType<typeof useFlowState>;
  /**
   * The hold, owned by the LAYOUT rather than by this screen.
   *
   * It used to live here, and that was the defect: the hold's id is what
   * `createBooking` consumes, and a hold scoped to this component does not
   * survive the walk to details and review — so nothing downstream could
   * commit. It is also genuinely flow-scoped rather than step-scoped: the
   * countdown keeps running while the client types their name, and the hold
   * expiring on Review has to be handled there.
   */
  hold: ReturnType<typeof useHold>;
}

function mmss(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, "0")}`;
}

function firstVisibleDay(days: readonly string[], date: string | null): number {
  const index = Math.max(0, days.indexOf(date ?? ""));
  return Math.min(Math.floor(index / 5) * 5, Math.max(0, days.length - 5));
}

// Step 2 of 4 — choose a professional-local day and time, then hold it.
//
// The step where the two 409s live, and where they mean opposite things.

function StepTime({ slug, profile, flow, hold, verification = false, unresolved = false }: Props) {
  const { t, fmt, locale } = useTranslation();
  usePageTitle(t("flow.time.heading"));
  const navigate = useNavigate();
  const { serviceId, date, startTime } = flow.selection;
  const summary = serviceSummary(profile, serviceId, fmt);
  const days = upcomingDays(profile.timezone, 21);
  const selectedDate = date && days.includes(date) ? date : null;
  const [firstDay, setFirstDay] = useState(() => firstVisibleDay(days, date));
  const lastDate = useRef(date);

  // Back/forward can restore a different date without passing through the
  // day button. A hold for the old date may never remain usable on the new one.
  useEffect(() => {
    if (lastDate.current === date) return;
    lastDate.current = date;
    void hold.release();
    if (startTime) flow.update({ startTime: null }, { replace: true });
    if (date) setFirstDay(firstVisibleDay(days, date));
  }, [date, startTime, hold.release, flow.update]);

  useEffect(() => {
    if (!date || selectedDate) return;
    // An out-of-window or malformed day in a shared URL is not an API date.
    // Remove the stale day/time rather than querying or holding it.
    void hold.release();
    flow.update({ date: null, startTime: null }, { replace: true });
  }, [date, selectedDate, hold.release, flow.update]);

  // Refetched on mount (cache-and-network): see useAvailableSlots.
  const { data, loading, error, refetch } = useAvailableSlots({
    professionalId: profile.professionalId,
    serviceId,
    date: selectedDate,
  });

  const matchingData = data?.getAvailableSlots.date === selectedDate ? data.getAvailableSlots : null;
  const timeZone = matchingData?.timezone ?? profile.timezone;
  const heldDate = hold.state.status === "held"
    ? new Intl.DateTimeFormat("en-CA", {
      timeZone: profile.timezone, year: "numeric", month: "2-digit", day: "2-digit",
    }).format(new Date(hold.state.hold.startTime))
    : null;
  const held = hold.state.status === "held" && selectedDate === heldDate &&
    startTime === hold.state.hold.startTime ? hold.state : null;

  /*
   * THE CLIENT'S OWN LIVE HOLD STAYS ON THE LIST.
   *
   * Availability omits held slots, including this client's own (observed on
   * Dev, 2026-10-05: the held 9:00 vanished after Back from Details while the
   * "Held" countdown showed and Continue worked). The hold is authoritative and
   * still theirs, so its time is merged back into the list, sorted and deduped,
   * and drawn selected. Only while the hold is LIVE for this date and time:
   * expired, released or refused, it is not injected.
   */
  const fetchedSlots = matchingData?.slots ?? [];
  const ownHeldStart = held ? held.hold.startTime : null;
  const slots =
    held && ownHeldStart && !fetchedSlots.some((slot) => slot.startTime === ownHeldStart)
      ? [...fetchedSlots, { startTime: held.hold.startTime, endTime: held.hold.endTime }].sort(
          (a, b) => Date.parse(a.startTime) - Date.parse(b.startTime)
        )
      : fetchedSlots;
  const hasSelectedSlot = slots.some((slot) => slot.startTime === startTime);

  // FRESH AVAILABILITY BEFORE A CHOICE (optimization review F02).
  // `cache-and-network` paints the cached list while the fresh read is in
  // flight, and that list may already be wrong: Back from Details showed two
  // cached times, both selectable, that the fresh answer then removed. Until
  // the fresh answer lands the list stays on screen (no layout jump) but is
  // inert. `aria-disabled` rather than `disabled`, so a focused time keeps its
  // focus; a click or arrow key (radioKeys clicks) does nothing. An existing
  // hold is untouched: Continue follows the hold, not this list.
  const checkingSlots = loading && slots.length > 0;

  // The hold expired. Product's rule: preserve completed details, clear ONLY
  // the time, return to time selection — which is where we already are, so the
  // list just needs to be current again.
  //
  // ONCE PER EXPIRY, by construction (IF-10, rc.7): the hold's `expired` state
  // object is created once per expiry, so the effect acts only on a state it
  // has not handled. A dependency changing identity later (the cause of IF-10's
  // second refetch) cannot make it refetch again.
  const expired = hold.state.status === "expired";
  const handledHoldState = useRef<object | null>(null);
  useEffect(() => {
    if (!expired || !selectedDate || handledHoldState.current === hold.state) return;
    handledHoldState.current = hold.state;
    flow.clearExpiredTime();
    void refetch();
  }, [expired, selectedDate, hold.state, flow.clearExpiredTime, refetch]);

  // SLOT_TAKEN means the list is out of date, so refresh it rather than leaving
  // a time on screen that cannot be booked.
  const taken = hold.state.status === "conflict" && hold.state.conflict === "SLOT_TAKEN";

  // NOT_FOUND means the slot is expired, missing or not ours. Different cause,
  // same consequence: the list on screen is wrong and the time cannot be held.
  // Refreshed for the same reason, and shown the generic failure below, because
  // saying "someone else booked it" would be inventing a cause we do not know.
  const gone = hold.state.status === "conflict" && hold.state.conflict === "NOT_FOUND";

  // Once per conflict, for the same reason as the expiry above.
  const handledConflict = useRef<object | null>(null);
  useEffect(() => {
    if (!(taken || gone) || !selectedDate || handledConflict.current === hold.state) return;
    handledConflict.current = hold.state;
    void refetch();
  }, [taken, gone, selectedDate, hold.state, refetch]);

  // Warm the guest identity on intent, once (lib/firebase.ts
  // warmGuestIdentity): a day tap, or pressing or focusing a slot when the day
  // came from the URL. Never on arrival, never on hover.
  const warmed = useRef(false);
  const warmIdentity = () => {
    if (warmed.current) return;
    warmed.current = true;
    warmGuestIdentity();
  };

  function chooseDay(day: string) {
    warmIdentity();
    if (day === selectedDate) return;
    // Invalidate an in-flight acquire synchronously, before the URL changes.
    lastDate.current = day;
    void hold.release();
    flow.chooseDate(day);
  }

  function showDays(nextFirstDay: number) {
    if (nextFirstDay === firstDay) return;
    // Paging may hide the currently selected day while leaving its slots and
    // held Continue on screen. A client browsing a new strip must never book
    // a date no longer visible. Clear the URL selection and release its hold
    // before displaying that strip; Back can restore the day, but not the hold.
    if (selectedDate && !days.slice(nextFirstDay, nextFirstDay + 5).includes(selectedDate)) {
      lastDate.current = null;
      void hold.release();
      flow.update({ date: null, startTime: null });
    }
    setFirstDay(nextFirstDay);
  }

  function pick(slotStart: string) {
    // A second tap on the already-held slot is not a new choice. Releasing it
    // and then acquiring again would manufacture a fresh expiry even though
    // the Backend's same-owner re-hold contract resumes the existing one.
    if (hold.state.status === "held" && hold.state.hold.startTime === slotStart &&
        startTime === slotStart && selectedDate === heldDate) return;
    flow.chooseTime(slotStart);
    void hold.acquire({
      professionalId: profile.professionalId,
      serviceId: serviceId as string,
      startTime: slotStart,
    });
  }

  const conflictTime =
    hold.state.status === "conflict" || hold.state.status === "expired" || hold.state.status === "failed" ||
    hold.state.status === "refused"
      ? hold.state.startTime
      : null;

  return (
    <FlowFrame
      testId="step-time"
      data={{ hold: hold.state.status }}
      lead={<StepLeadBar backTo={`/${slug}/book/service${window.location.search}`} step={2} of={verification ? 5 : 4} v3 />}
    >

      <div className="b-scroll">
        <div className="b-between">
          <div className="b-stack g6">
            <h1 className="b-display" tabIndex={-1}>{t("flow.time.heading")}</h1>
            {/* Web v2: the service rides under the heading. */}
            {summary ? <p className="b2-sub">{summary}</p> : null}
          </div>
          {held ? (
            <span className="b-pill t-hold" data-testid="hold-countdown" aria-live="polite">
              {t("flow.time.holding", { time: mmss(held.secondsLeft) })}
            </span>
          ) : null}
        </div>

        <div className="b2-day-picker">
          <div className="b3-calendar-head">
            <h2 data-testid="time-calendar-heading">{new Intl.DateTimeFormat(locale === "es" ? "es-US" : locale, { month: "long", year: "numeric", timeZone: "UTC" }).format(new Date(`${days[firstDay]}T12:00:00.000Z`))}</h2>
            <div className="b2-day-pages">
              <button type="button" data-testid="time-previous-days" aria-label={t("flow.time.previousDays")} disabled={firstDay === 0}
                onClick={() => showDays(Math.max(0, firstDay - 5))}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true"><path d="m15 18-6-6 6-6" /></svg></button>
              <button type="button" data-testid="time-next-days" aria-label={t("flow.time.nextDays")} disabled={firstDay + 5 >= days.length}
                onClick={() => showDays(Math.min(days.length - 5, firstDay + 5))}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true"><path d="m9 6 6 6-6 6" /></svg></button>
            </div>
          </div>
          <div className="b2-day-strip" role="radiogroup" aria-label={t("flow.date.heading")}
            data-testid="time-days">
            {days.slice(firstDay, firstDay + 5).map((day) => {
              const selected = day === selectedDate;
              const civil = new Date(`${day}T12:00:00.000Z`);
              return (
                <button key={day} type="button" role="radio" aria-checked={selected}
                  aria-label={fmt.date(civil.toISOString(), "UTC")}
                  tabIndex={selected || (!selectedDate && day === days[firstDay]) ? 0 : -1}
                  onKeyDown={radioKeys}
                  className={`b2-day${selected ? " is-on" : ""}`}
                  data-testid={`day-${day}`} onClick={() => chooseDay(day)}>
                  <span>{new Intl.DateTimeFormat(locale === "es" ? "es-US" : locale, { weekday: "short", timeZone: "UTC" }).format(civil)}</span>
                  <strong>{Number(day.slice(8))}</strong>
                </button>
              );
            })}
          </div>

        </div>

        {/* The two 409s, with copy that differs because the instruction does.
            SLOT_HELD is "wait a moment"; SLOT_TAKEN is "that one is gone".
            Branching on HTTP status would give the wrong one — both are 409. */}
        {hold.state.status === "conflict" && hold.state.conflict === "SLOT_HELD" ? (
          <div className="b-notice t-warning" role="alert" data-testid="conflict-held">
            <div className="b-notice-b">
              <b>{t("flow.time.held.title")}</b>
              <p className="b-p">{t("flow.time.held.body")}</p>
            </div>
          </div>
        ) : null}

        {taken && conflictTime ? (
          <div className="b-notice t-danger" role="alert" data-testid="conflict-taken">
            <div className="b-notice-b">
              <b>{t("flow.time.taken.title", { time: fmt.time(conflictTime, timeZone) })}</b>
              <p className="b-p">{t("flow.time.taken.body")}</p>
            </div>
          </div>
        ) : null}

        {hold.state.status === "expired" ? (
          <div className="b-notice t-warning" role="alert" data-testid="hold-expired">
            <div className="b-notice-b">
              {unresolved ? (
                <>
                  <b>{t("flow.review.uncertain.title")}</b>
                  <p className="b-p">{t("flow.review.uncertain.body")}</p>
                </>
              ) : <b>{t("flow.time.expired.title")}</b>}
              {!unresolved && t("flow.time.expired.body") ? <p className="b-p">
                {t("flow.time.expired.body", { time: fmt.time(hold.state.startTime, timeZone) })}
              </p> : null}
            </div>
          </div>
        ) : null}

        {/* `gone` rides with `failed` deliberately. Before the conflict parser
            was fixed, NOT_FOUND could not be read at all and fell through to
            this notice; once it COULD be read it matched no branch and the
            screen went silent — a worse outcome than the generic error it
            replaced. A typed state with no rendering is a dead end.

            The copy is imperfect for this case and that is a known gap: it
            mentions the connection, which is not why a slot is NOT_FOUND.
            Product owns a distinct string; until then the honest generic
            failure beats silence. */}
        {/* PAYMENT-MODE REFUSAL at the hold. Not the connection and not this
            slot: every time will be refused the same way, so the connection
            advice below would be false. The existing title only until Product
            supplies the wording. */}
        {hold.state.status === "refused" ? (
          <div className="b-notice t-danger" role="alert" data-testid="hold-payment-unavailable">
            <div className="b-notice-b">
              <b>{t("flow.time.failed.title")}</b>
            </div>
          </div>
        ) : null}

        {hold.state.status === "failed" || gone ? (
          <div className="b-notice t-danger" role="alert" data-testid="hold-failed">
            <div className="b-notice-b">
              <b>{t("flow.time.failed.title")}</b>
              {t("flow.time.failed.body") ? <p className="b-p">{t("flow.time.failed.body")}</p> : null}
            </div>
          </div>
        ) : null}

        {error ? (
          <LoadFailed variant="notice" title="profile.error.title" body="profile.error.body" retry="profile.error.retry"
            onRetry={() => void refetch()} testId="slots-error" />
        ) : null}

        {/* The time grid's shape while its times load, when nothing cached can
            be shown (D4); cached times keep the inert list (F02). */}
        {selectedDate && loading && slots.length === 0 ? <TimeGridSkeleton /> : null}

        {!selectedDate ? (
          <p className="b-p" data-testid="step-time-pick-day">{t("flow.date.pickADay")}</p>
        ) : null}

        {selectedDate && !loading && !error && slots.length === 0 ? (
          <div className="b-notice t-quiet" data-testid="step-time-none">
            <div className="b-notice-b">
              <b>{t("flow.time.none.title")}</b>
              <p className="b-p">{t("flow.time.none.body")}</p>
            </div>
          </div>
        ) : null}

        {slots.length > 0 ? (
          <section className="b3-time-group">
            {selectedDate ? <h2>{fmt.date(`${selectedDate}T12:00:00.000Z`, "UTC")}</h2> : null}
            <h3>{t("flow.time.pickATime")}</h3>
            <div className="b-slots" role="radiogroup" aria-label={t("flow.time.heading")} aria-busy={checkingSlots || undefined}>
              {slots.map((slot) => {
                const isSelected = slot.startTime === startTime;
                // The tapped time is being held: progress on the button itself (D4).
                const acquiring = hold.state.status === "acquiring";
                const holding = isSelected && acquiring;
                return (
                  <button
                    key={slot.startTime}
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    tabIndex={isSelected || (!hasSelectedSlot && slot === slots[0]) ? 0 : -1}
                    // While a hold is acquiring nothing else can be chosen, as when
                    // these were natively disabled, but the times stay FOCUSABLE:
                    // `disabled` dropped focus to <body> after Enter on a time
                    // (c6-0). No arrow-key moves mid-acquire either.
                    onKeyDown={acquiring ? undefined : radioKeys}
                    className={`b-slot${isSelected ? " is-on" : ""}`}
                    data-testid={`slot-${slot.startTime}`}
                    aria-busy={holding || undefined}
                    // The client's own live hold is never inert: it is authoritative,
                    // not a cached guess (F02 applies to everything else).
                    aria-disabled={acquiring || (checkingSlots && slot.startTime !== ownHeldStart) || undefined}
                    // Pointer-down and focus only: a mouseenter also fires when
                    // the times land under a cursor that is not moving, which
                    // is not intent (review P3, 2026-10-07).
                    onPointerDown={warmIdentity}
                    onFocus={warmIdentity}
                    onClick={() => {
                      if (acquiring) return;
                      if (!checkingSlots || slot.startTime === ownHeldStart) pick(slot.startTime);
                    }}
                  >
                    <span className="b-slot-label">
                      {holding ? <span className="b2-spin" aria-hidden="true" /> : null}
                      {fmt.time(slot.startTime, timeZone)}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* The appointment happens where the professional is. Said plainly,
                because a client in another timezone reading local times would
                arrive at the wrong hour. Under the times, as Web v2 draws it. */}
            <p className="b-meta" data-testid="step-time-zone">{t("flow.time.zoneNote", { business: profile.businessName })}</p>

            <p className="b-meta">
              {held ? t("flow.time.heldNote") : t("flow.time.nothingBookedYet")}
            </p>
          </section>
        ) : null}
        <StepMeta />
      </div>

      <StepFoot>
        {/* Enabled only while a hold is actually live — continuing without one
            would walk the client into entering their details for a slot nobody
            is keeping for them.

            This button was inert until 2026-09-01: it rendered, it enabled and
            disabled correctly, and it navigated nowhere, so the flow could not
            be walked past this step in a browser at all. Nothing caught it,
            because every test stopped at the hold and asserted this button's
            DISABLED state — which is exactly the assertion an unwired button
            passes. The commit-path spec in booking-flow.spec.js now walks
            through it. */}
        <button
          type="button"
          className={`b-btn v-primary is-full${held ? "" : " is-off"}`}
          data-testid="step-time-continue"
          disabled={!held}
          // `window.location.search` inside the handler, not during render:
          // the selection lives in the query string and must survive the step.
          onClick={() => {
            requestStepFocus();
            navigate(`/${slug}/book/details${window.location.search}`);
          }}
        >
          {t("flow.continue")}
        </button>
      </StepFoot>
    </FlowFrame>
  );
}

export default StepTime;
